import { apiStore } from '$lib/stores/api';
import { userStore } from '$lib/stores/user';
import type { Axios } from 'axios';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';
import { now } from './time';

type DoNotDisturbStore = {
	enabled: boolean;
	end: DateTime | null;
};

const doNotDisturbStoreDefinition = () => {
	const { subscribe, set } = writable<DoNotDisturbStore>({
		enabled: false,
		end: null
	});

	return {
		subscribe,
		set,
		enable: (date: DateTime) => {
			// Only enable is date is in the future
			if (date < get(now)) {
				return;
			}

			const currentDate = get(doNotDisturbStore).end;

			(get(apiStore) as Axios)
				.post('/user/do-not-disturb', { until: date.toISO() })
				.catch((error) => {
					console.error('Failed to set do not disturb', error);

					// Revert to previous date
					set({
						enabled: true,
						end: currentDate
					});
				});

			set({
				enabled: true,
				end: date
			});
		},
		disable: () => {
			const currentDate = get(doNotDisturbStore).end;

			(get(apiStore) as Axios).delete('/user/do-not-disturb').catch((error) => {
				console.error('Failed to clear do not disturb', error);

				// Revert to previous date
				set({
					enabled: true,
					end: currentDate
				});
			});

			set({
				enabled: false,
				end: null
			});
		},
		isActive: () => {
			// Get date from store, not from localStorage
			const { end } = get(doNotDisturbStore);
			if (!end) {
				return false;
			}

			return end > get(now);
		},
		getRemainingTime: () => {
			const doNotDisturb = get(doNotDisturbStore).end;
			if (!doNotDisturb || doNotDisturb < get(now)) {
				return 0;
			}

			const date = doNotDisturb.diffNow().toObject();
			return { seconds: date.seconds, minutes: date.minutes, hours: date.hours };
		}
	};
};

export const doNotDisturbStore = doNotDisturbStoreDefinition();

userStore.subscribe((user) => {
	if (!user) {
		return () => {};
	}
	// Refresh do not disturb status from user.doNotDisturb property
	if (user.doNotDisturbUntil && user.doNotDisturbUntil > get(now)) {
		doNotDisturbStore.set({
			enabled: true,
			end: user.doNotDisturbUntil
		});
	} else {
		doNotDisturbStore.set({
			enabled: false,
			end: null
		});
	}

	return () => {};
});
